import React, { useState } from 'react';
import { getDeviceInfo } from '../../utils/deviceDetection';
import { apiService } from '../../services/api';
import styles from '../../styles/workshop2026Page.module.css';

const WORKSHOP_QUESTION_TYPE = '2026 Workshop Question';
const WORKSHOP_LOCATION = '2026 SAA Step Workshop';

interface WorkshopFaqInlineAskProps {
  isSignedIn: boolean;
  onLoginForQuestion: () => void;
  userEmail?: string;
  userDisplayName?: string;
}

const WorkshopFaqInlineAsk: React.FC<WorkshopFaqInlineAskProps> = ({
  isSignedIn,
  onLoginForQuestion,
  userEmail,
  userDisplayName,
}) => {
  const [questionText, setQuestionText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  if (!isSignedIn) {
    return (
      <div className={styles.faqAskBlock}>
        <p className={styles.faqAnswerText}>
          Sign in to send a question to the workshop facilitators.
        </p>
        <button type="button" className={styles.primaryBtn} onClick={onLoginForQuestion}>
          Sign in to ask a question
        </button>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionText.trim() || !userEmail) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const askerLabel = userDisplayName?.trim()
      ? `${userDisplayName.trim()} <${userEmail}>`
      : userEmail;
    const feedbackText = `From: ${askerLabel}\n\nQuestion:\n${questionText.trim()}`;

    try {
      await apiService.submitFeedback({
        userEmail,
        locationInApp: WORKSHOP_LOCATION,
        feedbackType: WORKSHOP_QUESTION_TYPE,
        feedbackText,
        deviceInfo: getDeviceInfo(),
        emailCopyToUser: false,
      });
      setSubmitSuccess(true);
      setQuestionText('');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to send your question');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className={styles.faqAskBlock}>
        <p className={styles.faqAskSuccess}>Thank you — your question has been sent.</p>
        <button
          type="button"
          className={styles.textButton}
          onClick={() => setSubmitSuccess(false)}
        >
          Ask another question
        </button>
      </div>
    );
  }

  return (
    <form className={styles.faqAskBlock} onSubmit={handleSubmit}>
      <label className={styles.faqAskLabel} htmlFor="faqQuestionText">
        Your question
      </label>
      <textarea
        id="faqQuestionText"
        className={styles.faqAskTextarea}
        value={questionText}
        onChange={(e) => setQuestionText(e.target.value)}
        placeholder="What would you like to know?"
        rows={4}
        required
      />
      {submitError && <p className={styles.faqAskError}>{submitError}</p>}
      <button
        type="submit"
        className={styles.primaryBtn}
        disabled={!questionText.trim() || isSubmitting}
      >
        {isSubmitting ? 'Sending…' : 'Send question'}
      </button>
    </form>
  );
};

export default WorkshopFaqInlineAsk;
